// SGNL-205 — Deal Explorer saved-view chips.
//
// Above the deal table, render a horizontal chip strip representing
// saved filter presets. Clicking a chip:
//   1. Applies the chip's filter preset via setFilters
//   2. Updates the URL hash to #deals?view={chip.view} so the view is
//      bookmarkable
// On a fresh page load to #deals?view=lost, parseRoute returns
// kind="tab" with params={view: "lost"} and main.ts calls
// applyChipView("lost") which routes through here.
//
// Chips de-highlight when the user manually edits the filter bar away
// from the chip's preset (chipMatchesFilters returns false).
//
// SGNL-204 follow-ups (SGNL-207) will remove the dedicated tabs that
// these chips replace; v1 ships the chip layer alongside the existing
// tabs so the migration is gradual.

import { esc } from "../lib/format";
import { getState, setFilters } from "../state";
import type { Filters } from "../types";

export interface DealChip {
  /** URL-safe view key (e.g. "lost"). Used as the `?view=` param. */
  view: string;
  /** Display label. */
  label: string;
  /** Test-id suffix; rendered as `deals-chip-{testid}`. Defaults to view. */
  testid?: string;
  /** The filter patch applied when the chip is clicked. The "all" chip
   * uses an empty object — paired with reset semantics in applyChipView. */
  preset: Partial<Filters>;
}

/** Built-in chips for v1. Future SGNL-205 follow-ups can add
 * Farming/Hunting/Partner/Top Customers once their precise filter
 * mappings are designed with operator input. */
export const DEAL_CHIPS: readonly DealChip[] = Object.freeze([
  { view: "all", label: "All deals", preset: {} },
  { view: "open", label: "Open", preset: { state: "open" } },
  { view: "won", label: "Won", preset: { state: "won" } },
  { view: "lost", label: "Lost", preset: { state: "lost" } },
] as const);

/** Look up a chip by view key. Returns null for unknown views (e.g.
 * #deals?view=garbage) so callers can ignore them silently rather
 * than crash on a stale bookmark. */
export function findChip(view: string): DealChip | null {
  return DEAL_CHIPS.find((c) => c.view === view) ?? null;
}

/** True iff the current state.filters matches the chip's preset
 * exactly. Used to drive the "active" pill styling — when the user
 * manually edits the filter bar, the chip de-highlights.
 *
 * Match rule: the chip's preset keys must equal the current filter
 * values on those keys; other filter keys (BU, owner, tags, etc.) may
 * be anything — they're orthogonal axes. The "all" chip is active
 * when no `state` filter is set. */
export function chipMatchesFilters(chip: DealChip, filters: Filters): boolean {
  // The "all" chip — active when there's no state filter. Other filter
  // axes (BU, owner, etc.) are independent and don't affect chip-match.
  if (chip.view === "all") {
    return filters.state === undefined;
  }
  for (const key of Object.keys(chip.preset) as (keyof Filters)[]) {
    if (filters[key] !== chip.preset[key]) return false;
  }
  return true;
}

/** Find which chip (if any) matches the current state.filters. Returns
 * null when no preset matches — e.g. user has filter.state="open" AND
 * filter.bu="Tech" applied; "open" still matches because state==open. */
export function activeChipView(filters: Filters): string | null {
  // Prefer specific chips over "all" — iterate in declaration order
  // BUT prefer the first non-"all" match if present. "all" is the
  // weakest predicate (no state filter); if a stronger chip also
  // matches (which it can't unless preset rules drift), prefer it.
  for (const chip of DEAL_CHIPS) {
    if (chip.view === "all") continue;
    if (chipMatchesFilters(chip, filters)) return chip.view;
  }
  if (chipMatchesFilters(DEAL_CHIPS[0], filters)) return "all";
  return null;
}

/** Every filter axis any chip in DEAL_CHIPS uses. Used by applyChipView
 * to clear the union of axes before applying a specific chip's preset.
 *
 * SGNL-205 (CR F-04 / architect F-04): the clear step MUST derive from
 * the chip catalogue, not hardcode "state". When a future chip uses a
 * second axis (e.g. {bu: "Tech"} or {min_amount: 500000}), the old
 * hardcoded `setFilters({state: undefined, ...preset})` would leave
 * residual values from the previous chip on that axis. Recomputing the
 * union at module load (instead of per-call) is safe because the chip
 * catalogue is `readonly` and frozen at module init. */
const CHIP_AXES = Object.fromEntries(
  Array.from(new Set(DEAL_CHIPS.flatMap((c) => Object.keys(c.preset)))).map((k) => [
    k,
    undefined as undefined,
  ]),
) as Partial<Filters>;

/** Apply a chip's preset to state. Called both from the chip click
 * handler and from main.ts's hash-driven boot path (parseRoute →
 * kind:"tab" with view param).
 *
 * Clears every axis used by ANY chip before applying the requested
 * chip's preset. This makes the chip set mutually exclusive on all
 * axes they touch — clicking "Open" after a hypothetical multi-axis
 * "Top Customers" chip cleanly drops the lingering axes. */
export function applyChipView(view: string): void {
  const chip = findChip(view);
  if (!chip) return;
  setFilters({ ...CHIP_AXES, ...chip.preset });
}

/** Render the chip strip HTML. activeView highlights the matching chip
 * via the `active` class. Empty string / null = no chip active (user
 * has a custom filter mix). */
export function renderChipStrip(activeView: string | null): string {
  return `
    <div class="deal-chip-strip" data-testid="deal-chip-strip" role="group" aria-label="Deal Explorer saved views">
      ${DEAL_CHIPS.map(
        (chip) => `
        <button type="button"
                class="deal-chip ${activeView === chip.view ? "active" : ""}"
                data-action="deal-chip"
                data-chip-view="${esc(chip.view)}"
                data-testid="deal-chip-${esc(chip.testid ?? chip.view)}"
                aria-pressed="${activeView === chip.view}">
          ${esc(chip.label)}
        </button>`,
      ).join("")}
    </div>`;
}

/** Wire chip clicks to:
 *   - URL hash update FIRST (#deals?view={view} for bookmarkability)
 *   - then applyChipView (updates state.filters)
 *
 * Order matters (architect F-01): the synchronous setFilters → notify
 * → renderEverything chain inside applyChipView re-reads
 * window.location.hash. If the hash is updated AFTER state, the
 * re-entrant render sees a stale hash and can apply the WRONG chip
 * view (or revert the click). Updating the hash first means the
 * render sees the correct hash.
 *
 * The hash update uses history.replaceState so chip-clicking doesn't
 * pollute back-history with one entry per chip toggle. encodeURIComponent
 * on the view value is defensive against any future chip view key that
 * contains a non-ASCII or URL-reserved character (CR F-05). */
export function bindChipStrip(host: HTMLElement): void {
  for (const btn of host.querySelectorAll<HTMLButtonElement>(
    '[data-action="deal-chip"][data-chip-view]',
  )) {
    btn.addEventListener("click", () => {
      const view = btn.dataset.chipView;
      if (!view) return;
      // Update URL: #deals or #deals?view={view}. The "all" view drops
      // the param so the canonical Deal Explorer URL stays clean.
      const newHash =
        view === "all" ? "#deals" : `#deals?view=${encodeURIComponent(view)}`;
      if (window.location.hash !== newHash) {
        history.replaceState(
          history.state,
          "",
          window.location.pathname + window.location.search + newHash,
        );
      }
      applyChipView(view);
    });
  }
}

/** Read-only helper: current chip view from the current filters, or
 * null. Used by renderDealsTab to pass into renderChipStrip. */
export function currentChipView(): string | null {
  return activeChipView(getState().filters);
}
